import { t } from '@lingui/macro';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { AdminPages, buildPageTarget, buildPageTargetWithIdProp } from '../AdminLinkUtils';
import { monitoredElementsRenderer } from './CollectorsAdminCollectorListPanel';
import { dateTimeFormatter } from '../../../utilities';
import { AGENT_LAST_SEEN_AT_FIELD } from '../agents/AgentConstants';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { SORT_ASC } from '../../../utilities/FormUtilities';

export const COLLECTORS_FUNCTION_NAME = AdminPages.Collectors;

export const TAB_COLLECTORS = 'collectors';
export const TAB_IPSCOPES = 'ipScopes';

export const IP_SCOPES_FIELD = 'ipScopes';
export const COLLECTORS_ADMIN_NAME_FIELD = 'name';
export const COLLECTORS_ADMIN_STATUS_FIELD = 'status';
export const COLLECTORS_ADMIN_DEVICECOUNT_FIELD = 'deviceCount';
export const COLLECTORS_ADMIN_NETWORK_IPV4LOCAL_FIELD = 'network.ipv4Local';
export const COLLECTORS_ADMIN_NETWORK_IPV4EXTERNAL_FIELD = 'network.ipv4External';
export const COLLECTORS_ADMIN_NETWORK_HOSTNAME_FIELD = 'network.hostname';
export const COLLECTORS_ADMIN_NETWORK_MAC_FIELD = 'network.mac';
export const COLLECTORS_ADMIN_LASTSEENAT_FIELD = 'lastSeenAt';
export const COLLECTORS_ADMIN_LASTREGISTEREDAT_FIELD = 'lastRegisteredAt';
export const COLLECTORS_ADMIN_SOFTWAREVERSIONFMT_FIELD = 'softwareVersionFmt';
export const COLLECTORS_ADMIN_DESCRIPTION_FIELD = 'description';
export const COLLECTORS_ADMIN_ASSOCIATEDSITES_FIELD = 'associatedSites';
export const COLLECTORS_ADMIN_CREATEDAT_FIELD = 'createdAt';
export const COLLECTORS_ADMIN_UPDATEDAT_FIELD = 'updatedAt';
export const COLLECTORS_ADMIN_EMBEDDED_FIELD = 'embedded';

export const COLLECTORS_DESTCOLLECTOR_FIELD = 'destCollector';

export const COLLECTOR_LIST_COLUMNS = [
    {
        type: COLUMN_TYPES.LINK,
        mapping: COLLECTORS_ADMIN_NAME_FIELD,
        title: t`Name`,
        width: 225,
        sortable: true,
        link: {
            sysAdminOnly: true,
            target: buildPageTarget(COLLECTORS_FUNCTION_NAME),
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_STATUS_FIELD,
        title: t`Status`,
        sortable: true,
        width: 105,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        mapping: COLLECTORS_ADMIN_DEVICECOUNT_FIELD,
        title: t`Monitored Elements`,
        sortable: true,
        renderer: (row) => monitoredElementsRenderer(row),
        width: 175,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_NETWORK_IPV4EXTERNAL_FIELD,
        title: t`External IP`,
        sortable: true,
        width: 140,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_NETWORK_HOSTNAME_FIELD,
        title: t`Hostname`,
        sortable: true,
        width: 175,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_LASTSEENAT_FIELD,
        title: t`Last Seen`,
        sortable: true,
        width: 125,
        align: 'right',
        formatter: FORMATTER_TYPES.DURATIONSINCE,
        getTooltip: (row) => dateTimeFormatter(row[AGENT_LAST_SEEN_AT_FIELD]),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_LASTREGISTEREDAT_FIELD,
        title: t`Last Registered`,
        sortable: true,
        align: 'right',
        width: 125,
        formatter: FORMATTER_TYPES.DURATIONSINCE,
        getTooltip: (row) => dateTimeFormatter(row[AGENT_LAST_SEEN_AT_FIELD]),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_SOFTWAREVERSIONFMT_FIELD,
        title: t`Version`,
        sortable: true,
        width: 110,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_DESCRIPTION_FIELD,
        title: t`Description`,
        sortable: true,
        minWidth: 200,
        flex: 1,
    },
];

export const COLLECTOR_MAX_ELEMENTS_EMBEDDED = 25000;
export const COLLECTOR_MAX_ELEMENTS_DISTRIBUTED = 50000;

export const COLLECTOR_LIST_URL = '/ngp/v2/inventory/collectors';
export const COLLECTOR_LIST_SEARCH_FIELDS = [
    COLLECTORS_ADMIN_NAME_FIELD,
    COLLECTORS_ADMIN_DESCRIPTION_FIELD,
    COLLECTORS_ADMIN_STATUS_FIELD,
    COLLECTORS_ADMIN_NETWORK_HOSTNAME_FIELD,
    COLLECTORS_ADMIN_NETWORK_IPV4LOCAL_FIELD,
    COLLECTORS_ADMIN_NETWORK_MAC_FIELD,
    COLLECTORS_ADMIN_ASSOCIATEDSITES_FIELD,
    COLLECTORS_ADMIN_SOFTWAREVERSIONFMT_FIELD,
];
export const COLLECTOR_LIST_DATA_SOURCE = {
    url: COLLECTOR_LIST_URL,
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: COLLECTOR_LIST_SEARCH_FIELDS,
    },
};

export const COLLECTOR_LIST_REQUEST_PARAMS = {
    requestParams: JSON.stringify({
        entityFields: COLLECTOR_LIST_SEARCH_FIELDS,
        sort: COLLECTORS_ADMIN_NAME_FIELD,
        rowStart: 0,
        rowLimit: 500,
    }),
};
export const COLLECTOR_INITIAL_SORT_FIELD_INFO = [{ property: COLLECTORS_ADMIN_NAME_FIELD, direction: SORT_ASC }];

export const COLLECTORS_ADMIN_VALUE_SUBNET_FIELD = 'value.subnet';
export const COLLECTORS_ADMIN_COLLECTOR_NAME_FIELD = 'collector.name';
export const COLLECTORS_ADMIN_EXCLUDE_FIELD = 'exclude';
export const COLLECTORS_ADMIN_DISCOVER_FIELD = 'discover';
export const COLLECTORS_ADMIN_MONITOREDDEVICECOUNT_FIELD = 'monitoredDeviceCount';

export const EDIT_SCOPE_SUBNET_QUERY_PARAM = 'editScopeSubnet';

export const getIpScopeListColumns = (onEditIpScope) => [
    {
        type: COLUMN_TYPES.LINK,
        mapping: COLLECTORS_ADMIN_VALUE_SUBNET_FIELD,
        title: t`IP Scope`,
        tooltip: t`The subnet for this IP scope`,
        width: 225,
        sortable: true,
        sortProperty: 'value.intAddr.start',
        link: {
            sysAdminOnly: true,
            ...(typeof onEditIpScope === 'function'
                ? { clickHandler: (row) => onEditIpScope(row) }
                : { target: buildPageTargetWithIdProp(COLLECTORS_FUNCTION_NAME, 'collector._id') }),
        },
    },
    {
        type: COLUMN_TYPES.LINK,
        mapping: COLLECTORS_ADMIN_COLLECTOR_NAME_FIELD,
        title: t`Collector`,
        width: 225,
        sortable: true,
        link: {
            sysAdminOnly: true,
            target: buildPageTargetWithIdProp(COLLECTORS_FUNCTION_NAME, 'collector._id'),
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_EXCLUDE_FIELD,
        title: t`Type`,
        tooltip: t`Whether this IP scope is monitored or excluded`,
        sortable: true,
        width: 105,
        formatter: (value) => (value === true ? t`Excluded` : t`Monitored`),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_DISCOVER_FIELD,
        title: t`Discovering`,
        tooltip: t`If this IP scope will be scanned for new devices`,
        sortable: true,
        width: 105,
        formatter: (value, row) => {
            if (row.exclude) {
                return '--';
            }
            return value === true ? 'Yes' : 'No';
        },
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        mapping: COLLECTORS_ADMIN_MONITOREDDEVICECOUNT_FIELD,
        title: t`Monitored Elements`,
        tooltip: t`The number of monitored elements in this IP scope`,
        sortable: true,
        width: 175,
        formatter: (v, r) => (r.exclude ? '--' : v),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_DESCRIPTION_FIELD,
        title: t`Description`,
        sortable: true,
        flex: 1,
    },
];

export function ipScopeListDataSource() {
    return {
        url: `/ngp/v2/inventory/collector-scopes`,
        totalProperty: 'count',
        dataProperty: 'data',
        params: {
            searchFields: [
                COLLECTORS_ADMIN_VALUE_SUBNET_FIELD,
                COLLECTORS_ADMIN_COLLECTOR_NAME_FIELD,
                COLLECTORS_ADMIN_DESCRIPTION_FIELD,
            ],
        },
    };
}

export const getIpScopeListInDetailColumns = (onEditIpScope) => [
    {
        type: COLUMN_TYPES.LINK,
        mapping: COLLECTORS_ADMIN_VALUE_SUBNET_FIELD,
        title: t`IP Scope`,
        tooltip: t`The subnet for this IP scope`,
        width: 225,
        sortable: true,
        link: {
            sysAdminOnly: true,
            ...(typeof onEditIpScope === 'function'
                ? { clickHandler: (row) => onEditIpScope(row) }
                : { target: buildPageTarget(COLLECTORS_FUNCTION_NAME) }),
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_EXCLUDE_FIELD,
        title: t`Type`,
        tooltip: t`Whether this IP scope is monitored or excluded`,
        sortable: true,
        width: 105,
        formatter: (value) => (value === true ? t`Excluded` : t`Monitored`),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_DISCOVER_FIELD,
        title: t`Discovering`,
        tooltip: t`If this IP scope will be scanned for new devices`,
        sortable: true,
        width: 105,
        formatter: (value, row) => {
            if (row.exclude) {
                return '--';
            }
            return value === true ? 'Yes' : 'No';
        },
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        mapping: COLLECTORS_ADMIN_MONITOREDDEVICECOUNT_FIELD,
        title: t`Monitored Elements`,
        tooltip: t`The number of monitored elements in this IP scope`,
        sortable: true,
        width: 175,
        formatter: (v, r) => (r.exclude ? '--' : v),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: COLLECTORS_ADMIN_DESCRIPTION_FIELD,
        title: t`Description`,
        tooltip: t`The description of this IP scope`,
        sortable: true,
        flex: 1,
    },
];

export const VCENTERS_ADMIN_VALUE_NAME_FIELD = 'name';
export const VCENTERS_ADMIN_VALUE_IPADDRESS_FIELD = 'ipAddress';
export const VCENTERS_ADMIN_VALUE_VIRTUALIZATION_ENABLEDENTITYCOUNT_FIELD = 'virtualization.enabledEntityCount';
export const VCENTERS_ADMIN_VALUE_DESCRIPTION_FIELD = 'description';
